import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import moment from 'moment'; // Import moment for date formatting
import axios from 'axios'; // Import axios for making API requests
import { db } from '../../firebase/config';

const createEmailContent = (blog) => {
    const blogUrl = `https://confessionsofanutritionfreak.co.ke/blog/${blog.id}`;

    return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${blog.title}</title>
    <style>
        /* Reset styles */
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }

        /* Base styles */
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
            line-height: 1.6;
            color: #2D3748;
            background-color: #F7FAFC;
            margin: 0;
            padding: 0;
        }

        /* Container */
        .wrapper {
            max-width: 680px;
            margin: 0 auto;
            padding: 20px;
        }

        .container {
            background-color: #FFFFFF;
            border-radius: 12px;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
            overflow: hidden;
        }

        /* Header */
        .header {
            background: linear-gradient(135deg, #22C55E 0%, #16A34A 100%);
            color: white;
            padding: 40px 20px;
            text-align: center;
        }

        .header h1 {
            font-size: 28px;
            font-weight: 800;
            margin-bottom: 10px;
            letter-spacing: -0.5px;
        }

        .header p {
            font-size: 16px;
            opacity: 0.9;
        }

        /* Logo */
        .logo {
            width: 120px;
            height: auto;
            margin-bottom: 20px;
        }

        /* Content */
        .content {
            padding: 40px 30px;
            background-color: white;
        }

        .content h2 {
            color: #1A202C;
            font-size: 24px;
            font-weight: 700;
            margin-bottom: 20px;
            line-height: 1.4;
        }

        .content p {
            color: #4A5568;
            font-size: 16px;
            line-height: 1.8;
            margin-bottom: 20px;
        }

        /* Article Card */
        .article-card {
            border: 1px solid #E2E8F0;
            border-radius: 8px;
            padding: 20px;
            margin: 20px 0;
            background-color: #F8FAFC;
        }

        .article-image {
            width: 100%;
            border-radius: 6px;
            margin-bottom: 20px;
            height: auto;
        }

        /* Call to Action Button */
        .cta-button {
            display: inline-block;
            background: #22C55E;
            color: white;
            padding: 14px 28px;
            border-radius: 6px;
            text-decoration: none;
            font-weight: 600;
            margin: 20px 0;
            transition: background-color 0.3s ease;
        }

        .cta-button:hover {
            background: #16A34A;
        }

        /* Meta Information */
        .meta-info {
            display: flex;
            align-items: center;
            margin: 20px 0;
            color: #64748B;
            font-size: 14px;
        }

        .meta-info span {
            margin-right: 20px;
            display: flex;
            align-items: center;
        }

        /* Footer */
        .footer {
            background-color: #F8FAFC;
            padding: 30px;
            text-align: center;
            border-top: 1px solid #E2E8F0;
        }

        .footer p {
            color: #64748B;
            font-size: 14px;
            margin: 10px 0;
        }

        .footer a {
            color: #22C55E;
            text-decoration: none;
        }

        .social-links {
            margin: 20px 0;
        }

        .social-links a {
            display: inline-block;
            margin: 0 10px;
            color: #64748B;
            text-decoration: none;
        }

        /* Responsive Design */
        @media only screen and (max-width: 600px) {
            .wrapper {
                padding: 10px;
            }
            
            .content {
                padding: 20px;
            }
            
            .header h1 {
                font-size: 24px;
            }
            
            .content h2 {
                font-size: 20px;
            }
        }
    </style>
</head>
<body>
    <div class="wrapper">
        <div class="container">
            <div class="header">
                <h1>Confessions of a Nutrition Freak</h1>
                <p>Your Weekly Dose of Nutrition Wisdom</p>
            </div>
            
            <div class="content">
                <div class="article-card">
                    <img src="${blog.heroImage}" alt="Featured Article Image" class="article-image" />
                    <h2>${blog.excerpt}</h2>
                    
                    <div class="meta-info">
                        <span>📅 Published: ${moment(blog.createdAt).toString()}</span>
                        <span>⏱️${blog.readTime}</span>
                    </div>
                    
                    ${blog.content}
                    
                    <a href="${blogUrl}" class="cta-button">Read Full Article</a>
                </div>
                
                <div style="text-align: center; margin: 40px 0;">
                    <h3 style="color: #1A202C; margin-bottom: 20px;">Share with Your Friends</h3>
                    <div class="social-links">
                        <a href="#">Twitter</a>
                        <a href="#">Facebook</a>
                        <a href="#">LinkedIn</a>
                    </div>
                </div>
            </div>
            
            <div class="footer">
                <p>© 2024 Confessions of a Nutrition Freak. All rights reserved.</p>
                <p>You're receiving this email because you subscribed to our newsletter.</p>
                <p><a href="#">Update preferences</a> · <a href="<a href="https://confessionsofanutritionfreak.co.ke/unsubscribe/{{email}}">Unsubscribe</a></p>
                
                <p><small>Add newsletter@confessionsofanutritionfreak.co.ke to your address book to ensure delivery.</small></p>
            </div>
        </div>
    </div>
</body>
</html>`;
};
// src/redux/features/emailSlice.js
const emailAxios = axios.create({
    baseURL: 'https://mailing.confessionsofanutritionfreak.co.ke',
    headers: {
        'Content-Type': 'application/json'
    },
    withCredentials: false  // Add this
});

// Async thunk to send emails
export const sendEmails = createAsyncThunk(
    'emails/sendEmails',
    async ({ subscribers, blogData, emailType }, { rejectWithValue }) => {
        try {
            const emailCollection = collection(db, 'emails');
            const sentAt = new Date().toISOString();

            const emailPromises = subscribers.map(async (subscriber) => {
                const emailData = {
                    email: subscriber.email,
                    subject: emailType === 'new' ? `Blog: ${blogData.title}` : `Updated Blog: ${blogData.title}`,
                    message: createEmailContent(blogData),
                };

                // Send email using a service like SendGrid, Mailgun, etc.
                const response = await emailAxios.post('/', emailData);
                console.log('Test email response:', response.data);
                if (response.status !== 200 && response.status !== 202) {
                    throw new Error(`Failed to send email to ${subscriber.email}: ${response.status} - ${response.statusText}`);
                }

                // Log email send information in database (status sent)
                return addDoc(emailCollection, {
                    to: subscriber.email,
                    subject: emailData.subject,
                    content: emailData.html,
                    sentAt: serverTimestamp(),
                    blogId: blogData.id,
                    status: 'pending',
                });
            });

            await Promise.all(emailPromises);

            return { sentAt, emailCount: subscribers.length, blogId: blogData.id, emailType };
        } catch (error) {
            console.error('Error sending emails:', error);
            // Handle specific errors to determine failure type; example, email invalid, etc.
            return rejectWithValue(error.message || 'Failed to send emails');
        }
    }
);

export const sendSubriberWelcomeEmail = createAsyncThunk(
    'emails/sendSubscriberWelcomeEmail',
    async ({ subscriber }) => {
        try {
            const emailData = {
                email: subscriber.email,
                subject: 'Welcome to Confessions of a Nutrition Freak',
                message: `
                    <!DOCTYPE html>
                    <html lang="en">
                    <head>
                        <meta charset="UTF-8">
                        <meta name="viewport" content="width=device-width, initial-scale=1.0">
                        <title>Welcome to Confessions of a Nutrition Freak</title>
                        <style>
                            body {
                                font-family: Arial, sans-serif;
                                line-height: 1.6;
                                color: #333;
                                max-width: 600px;
                                margin: 0 auto;
                                padding: 20px;
                                background-color: #f4f4f4;
                            }
                            .container {
                                background-color: white;
                                border-radius: 8px;
                                box-shadow: 0 4px 6px rgba(0,0,0,0.1);
                                padding: 30px;
                            }
                            .header {
                                background-color: green;
                                color: white;
                                text-align: center;
                                padding: 15px;
                                border-radius: 8px 8px 0 0;
                            }
                            .content {
                                margin-top: 20px;
                                text-align: left;
                                padding: 15px;
                            }
                            .footer {
                                margin-top: 30px;
                                text-align: center;
                                font-size: 0.8em;
                                color: #777;
                                border-top: 1px solid #eee;
                                padding-top: 15px;
                            }
                            .logo {
                                max-width: 200px;
                                height: auto;
                                margin: 0 auto 15px;
                                display: block;
                            }
                        </style>
                    </head>
                    <body>
                        <div class="container">
                            <div class="header">
                                <h1>Welcome to Confessions of a Nutrition Freak</h1>
                            </div>
                            <div class="content">
                                <h2>Hello,</h2>
                                <p>Thank you for subscribing to our newsletter! We're excited to have you on board.</p>
                                <p>Stay tuned for the latest updates, tips, and articles on nutrition and healthy living.</p>
                            </div>
                            <div class="footer">
                                <p>© 2024 Confessions of a Nutrition Freak<br>
                                You're receiving this email because you subscribed to our newsletter.</p>
                                <p><small>To ensure delivery, please add newsletter@confessionsofanutritionfreak.co.ke to your address book.</small></p>
                            </div>
                        </div>
                    </body>
                    </html>
                `,
            };

            const response = await axios.post('https://mailing.confessionsofanutritionfreak.co.ke/', emailData, {
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            return response.data;
        } catch (error) {
            throw new Error('Failed to send welcome email');
        }
    }
);

// Email slice
const emailSlice = createSlice({
    name: 'emails',
    initialState: {
        status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
        sentEmails: [], // Store sent email metadata
        error: null,
    },
    reducers: {
        clearEmailStatus: (state) => {
            state.status = 'idle';
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(sendEmails.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(sendEmails.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.sentEmails.push(action.payload);
            })
            .addCase(sendEmails.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload || 'Failed to send emails';
            })
            .addCase(sendSubriberWelcomeEmail.fulfilled, (state) => {
                state.status = 'succeeded';
            })
            .addCase(sendSubriberWelcomeEmail.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload || 'Failed to send welcome email';
            });
    },
});

export const { clearEmailStatus } = emailSlice.actions;

export default emailSlice.reducer;
